import { hashKey, type QueryClient, type QueryKey } from "@tanstack/react-query";

/** What a successful write did to the records the workspace caches. */
export interface WorkspaceWrite {
  /** Detail queries to fill with the server's answer; they are not refetched. */
  set?: readonly (readonly [key: QueryKey, value: unknown])[];
  /** Exact keys of records the write deleted; they are never refetched. */
  remove?: readonly QueryKey[];
}

/**
 * Drops a deleted record. While a page still shows it (it navigates away right after
 * the delete) the query stays until its last observer leaves: removing it under a
 * mounted `useQuery` makes that observer build it again and fetch it into a 404.
 */
const forget = (client: QueryClient, queryKey: QueryKey): void => {
  const cache = client.getQueryCache();
  const query = cache.find({ queryKey, exact: true });
  if (!query) return;
  if (query.getObserversCount() === 0) {
    cache.remove(query);
    return;
  }
  void query.cancel();
  const unsubscribe = cache.subscribe((event) => {
    if (event.query !== query) return;
    if (event.type === "removed") unsubscribe();
    else if (event.type === "observerRemoved" && query.getObserversCount() === 0) {
      unsubscribe();
      cache.remove(query);
    }
  });
};

/**
 * Applies a write to the cache: fills and drops the touched records, then refreshes
 * the rest of the workspace (lists embed figures of related records) without waiting
 * for it, so the control that started the write is free as soon as the server answers.
 */
export const applyWorkspaceWrite = (
  client: QueryClient,
  workspace: QueryKey,
  write: WorkspaceWrite,
): void => {
  const settled = new Set<string>();
  for (const [key, value] of write.set ?? []) {
    client.setQueryData(key, value);
    settled.add(hashKey(key));
  }
  for (const key of write.remove ?? []) {
    forget(client, key);
    settled.add(hashKey(key));
  }
  void client.invalidateQueries({
    queryKey: workspace,
    predicate: (query) => !settled.has(query.queryHash),
  });
};
